import type { VariantProps } from 'class-variance-authority';
import { useRef, useState, type MouseEvent, type ReactNode } from 'react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/shadcn/alert-dialog';
import { Button } from '@/shared/components/shadcn/button';
import { buttonVariants } from '@/shared/components/shadcn/button/buttonVariants';

export type GlobalConfirmButtonOptions = {
  children?: ReactNode;
  variant?: VariantProps<typeof buttonVariants>['variant'];
  className?: string;
};

interface Props {
  isOpen: boolean;
  title: ReactNode;
  description: ReactNode;
  cancelText?: ReactNode;
  confirmButton?: GlobalConfirmButtonOptions;
  onUnmount?: () => void;
  onConfirm: () => Promise<void>;
  onConfirmSuccess: () => void;
  onCancel: () => void;
}

export function GlobalConfirmPopup({
  isOpen,
  title,
  description,
  onUnmount,
  cancelText = 'Cancel',
  confirmButton,
  onConfirm,
  onConfirmSuccess,
  onCancel,
}: Props) {
  const [isPending, setIsPending] = useState(false);
  const isPendingRef = useRef(false);

  const handleConfirm = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (isPendingRef.current) return;

    isPendingRef.current = true;
    setIsPending(true);

    try {
      await onConfirm();
      onConfirmSuccess();
    } catch {
      isPendingRef.current = false;
      setIsPending(false);
    }
  };

  const handleCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (isPendingRef.current) return;
    onCancel();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isPendingRef.current) {
      onCancel();
    }
  };

  const handleAnimationEnd = (event: React.AnimationEvent) => {
    if (!isOpen && event.animationName === 'exit') {
      onUnmount?.();
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent onAnimationEnd={handleAnimationEnd}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" disabled={isPending} onClick={handleCancel}>
            {cancelText}
          </Button>
          <Button
            loading={isPending}
            variant={confirmButton?.variant}
            className={confirmButton?.className}
            onClick={handleConfirm}
          >
            {confirmButton?.children ?? 'Confirm'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
